import { createServerFn } from "@tanstack/react-start";

import {
  createWebhook,
  deleteWebhook,
  getWebhook,
  listWebhooks,
  resendWebhookVerification,
  rotateWebhookSecret,
  testWebhook,
  updateWebhook,
  verifyWebhook,
} from "@/features/webhooks/server";
import { mapWebhookToView, type WebhookEventType } from "@/features/webhooks/view-model";

function passthroughValidator<T>(value: T) {
  return value;
}

export const getWebhooksView = createServerFn({ method: "GET" }).handler(async () => {
  const webhooks = await listWebhooks();
  return webhooks.map(mapWebhookToView);
});

export const createWebhookAction = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{ url: string; description: string; eventTypes: WebhookEventType[] }>,
  )
  .handler(async ({ data }) => {
    const created = await createWebhook({
      url: data.url,
      description: data.description || null,
      eventTypes: data.eventTypes,
    });

    return {
      webhook: mapWebhookToView(created),
      secret: created.secret,
    };
  });

export const updateWebhookAction = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      webhookId: string;
      url: string;
      description: string;
      eventTypes: WebhookEventType[];
      isActive: boolean;
    }>,
  )
  .handler(async ({ data }) => {
    const current = await getWebhook(data.webhookId);
    const nextStatus = data.isActive ? "active" : "disabled";
    const updated = await updateWebhook(data.webhookId, {
      url: data.url,
      description: data.description || null,
      event_types: data.eventTypes,
      ...(current.status !== "pending_verification" && current.status !== nextStatus
        ? { status: nextStatus }
        : {}),
    });

    return mapWebhookToView(updated);
  });

export const verifyWebhookAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ webhookId: string; verificationCode: string }>)
  .handler(async ({ data }) => {
    const verified = await verifyWebhook(data.webhookId, data.verificationCode);
    return mapWebhookToView(verified);
  });

export const resendWebhookVerificationAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ webhookId: string }>)
  .handler(async ({ data }) => {
    const resent = await resendWebhookVerification(data.webhookId);
    return mapWebhookToView(resent);
  });

export const deleteWebhookAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ webhookId: string }>)
  .handler(async ({ data }) => {
    await deleteWebhook(data.webhookId);
    return { success: true };
  });

export const rotateWebhookSecretAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ webhookId: string }>)
  .handler(async ({ data }) => {
    return await rotateWebhookSecret(data.webhookId);
  });

export const testWebhookAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ webhookId: string }>)
  .handler(async ({ data }) => {
    await testWebhook(data.webhookId);
    return { success: true };
  });
